import {Guide, GuideStep, StepPayload} from '@blesk-frontend/api';
import {
  Button,
  cn,
  createStep,
  cropImage,
  FieldErrorMessage,
  getMotionProps,
  hasUserViewport,
  ModalConfirm,
  plainTextPreset,
  StepDivider,
  StepErrorComponent,
  StepProvider,
  StepRenderer,
  StepToolbarRight,
  type StepViewportUpdate,
  Tiptap,
  Topbar,
  titleTextPreset,
  useModal,
} from '@blesk-frontend/ui';
import {createId} from '@paralleldrive/cuid2';
import {sendToBackgroundViaRelay} from '@plasmohq/messaging';
import {ErrorBoundary} from '@sentry/react';
import {useBlocker} from '@tanstack/react-router';
import {ChevronLeft} from 'lucide-react';
import {AnimatePresence, motion} from 'motion/react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {renderToString} from 'react-dom/server';
import {Controller, useFieldArray, useForm} from 'react-hook-form';

import {Route} from '@/routes/_app/guides/$guideId.edit';

export interface GuideFormData extends Pick<Guide, 'name' | 'description' | 'steps'> {}

interface GuideFormProps {
  guide?: Guide;
  onSubmit: (data: GuideFormData) => Promise<void>;
}

const stepMotionProps = getMotionProps(
  {
    initial: {opacity: 0, height: 0},
    animate: {opacity: 1, height: 'auto'},
    exit: {opacity: 0, height: 0, transition: {delay: 0.35, duration: 0.35}},
  },
  {duration: 0.2},
);

const stepEntryMotionProps = getMotionProps(
  {
    initial: {x: -24, opacity: 0},
    animate: {x: 0, opacity: 1},
    exit: {x: -24, opacity: 0, transition: {delay: 0, duration: 0.2}},
  },
  {delay: 0.2, duration: 0.2},
);

export const GuideForm = ({guide, onSubmit}: GuideFormProps) => {
  const navigate = Route.useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepsViewport, setStepsViewport] = useState<Map<number, StepViewportUpdate>>();
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const {
    formState: {errors, isDirty},
    control,
    reset,
    handleSubmit,
  } = useForm<GuideFormData>({
    defaultValues: {
      name: `Новая инструкция ${new Date()
        .toLocaleString('ru-RU', {dateStyle: 'short', timeStyle: 'short'})
        .replace(',', '')}`,
      description: '',
      ...guide,
      steps: guide?.steps ? [...guide.steps].sort((a, b) => a.position - b.position) : [],
    },
  });

  const blocker = useBlocker({
    condition: isDirty,
  });

  const {fields, insert, swap, update, remove} = useFieldArray({
    control,
    name: 'steps',
    keyName: '_',
  });

  const visibleFields = useMemo(() => fields.filter((step) => !step.payload.meta.hidden), [fields]);

  const openConfirmLeaveModal = useModal((props) => (
    <ModalConfirm
      {...props}
      title="Выйти без сохранения"
      message="Подтвердите, что хотите выйти не сохраняя изменения."
      labels={{cancel: 'Остаться', confirm: 'Выйти'}}
      onConfirm={blocker.proceed}
      onClose={() => {
        blocker.reset?.();
        props.onClose?.();
      }}
    />
  ));

  const onStepMove = useCallback(
    (from: number, to: number) => {
      if (to <= -1 || to >= fields.length) return;

      const toSwapStep = fields[to];

      if (toSwapStep && from < to) {
        requestAnimationFrame(() => {
          const stepElement = document.querySelector(`[data-step-id="${toSwapStep.id}"]`);
          stepElement?.scrollIntoView({block: 'center', behavior: 'smooth'});
        });
      }

      swap(from, to);
    },
    [fields, swap],
  );

  const onViewportUpdate = useCallback((index: number, update: StepViewportUpdate) => {
    setStepsViewport((prev) => (prev || new Map()).set(index, update));
  }, []);

  const onStepUpdate = useCallback(
    (index: number, step: GuideStep) => {
      const editorChanged = fields[index]?.payload.editor !== step.payload.editor;
      update(index, step);

      if (editorChanged && !hasUserViewport(step.payload.editor)) {
        setStepsViewport((prev) => {
          if (!prev?.has(index)) return prev;
          const next = new Map(prev);
          next.delete(index);
          return next;
        });
      }
    },
    [fields, update],
  );

  const onStepDuplicate = useCallback(
    (index: number) => {
      const currentStep = {...fields[index]};
      const newStep = {
        ...currentStep,
        id: createId(),
        position: currentStep.position + 0.1,
        payload: {...currentStep.payload, id: createId()},
      };
      insert(index + 1, newStep, {shouldFocus: false});
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        const stepElement = document.querySelector(`[data-step-id="${newStep.id}"]`);
        if (stepElement) {
          requestAnimationFrame(() => {
            stepElement.scrollIntoView({block: 'center', behavior: 'smooth'});
            (
              stepElement.querySelector('div[contenteditable="true"]') as HTMLElement | undefined
            )?.focus();
          });
        }
      }, 200);
    },
    [fields, insert],
  );

  const onStepDelete = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  const onClickAdd = useCallback(
    (after: number) => {
      const newStep = createStep({position: after + 0.1});
      insert(after + 1, newStep, {shouldFocus: false});
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        const stepElement = document.querySelector(`[data-step-id="${newStep.id}"]`);
        if (stepElement) {
          requestAnimationFrame(() => {
            stepElement.scrollIntoView({block: 'center', behavior: 'smooth'});
            (
              stepElement.querySelector('div[contenteditable="true"]') as HTMLElement | undefined
            )?.focus();
          });
        }
      }, 200);
    },
    [insert],
  );

  const onClickAddBrowser = useCallback(async (index: number) => {
    await sendToBackgroundViaRelay({
      // @ts-expect-error
      name: 'open-side-panel',
      body: {
        createStepsAfter: index,
      },
    });
  }, []);

  const onClickBack = () => {
    if (guide?.id) {
      navigate({to: '/guides/$guideId', params: {guideId: guide.id}});
    } else {
      navigate({to: '/guides'});
    }
  };

  const onFormSubmit = handleSubmit(async (data: GuideFormData) => {
    setIsSubmitting(true);
    const dataWithViewports: GuideFormData = {
      ...data,
      steps: data.steps.map((step, index) => {
        const viewportUpdate = stepsViewport?.get(index);
        return {
          ...step,
          payload: {
            ...step.payload,
            editor: {
              ...step.payload.editor,
              zoom: viewportUpdate?.zoom ?? step.payload.editor?.zoom,
              pan: viewportUpdate?.pan ?? step.payload.editor?.pan,
              viewport: viewportUpdate?.viewport ?? step.payload.editor?.viewport,
            },
          },
          position: index + 1,
        };
      }),
    };

    const dataWithCroppedImages = {
      ...dataWithViewports,
      steps: await Promise.all(
        dataWithViewports.steps.map(async (step) => ({
          ...step,
          payload: {
            ...step.payload,
            src: {
              ...step.payload.src,
              cropped: await cropImage(step.payload),
            },
          } satisfies StepPayload,
        })),
      ),
    };

    reset(dataWithCroppedImages);
    await onSubmit(dataWithCroppedImages);
    setIsSubmitting(false);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (blocker.status === 'blocked') {
      openConfirmLeaveModal();
    }
  }, [blocker.status]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data.name === 'guide/add-steps') {
        event.data.body.steps.forEach((step: GuideStep, index: number) => {
          insert(index + event.data.body.createStepsAfter, step, {shouldFocus: false});
        });
      }
    },
    [insert],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  return (
    <form onSubmit={onFormSubmit}>
      <Topbar variant="sticky">
        <Button variant="outline" size="icon" onClick={onClickBack}>
          <ChevronLeft className="size-4" />
        </Button>

        <div className="ml-auto flex items-center gap-4 text-text-secondary">
          <Button type="submit" loading={isSubmitting}>
            Завершить редактирование
          </Button>
        </div>
      </Topbar>

      <div
        className={cn(
          'prose prose-sm md:prose-base mx-auto max-w-3xl pb-64 outline-0',
          isSubmitting && 'pointer-events-none',
        )}
      >
        <div className="mb-6">
          <Controller
            control={control}
            name="name"
            rules={{required: 'Обязательное поле'}}
            render={({field}) => (
              <Tiptap
                {...field}
                className="focus-within:bg-bg-secondary-hover hover:bg-bg-secondary-hover"
                preset={titleTextPreset({placeholder: 'Название инструкции'})}
              />
            )}
          />
          <FieldErrorMessage errors={errors} name="name" />
        </div>

        <div className="mb-6">
          <Controller
            control={control}
            name="description"
            render={({field}) => {
              return (
                <Tiptap
                  {...field}
                  className="focus-within:bg-bg-secondary-hover hover:bg-bg-secondary-hover"
                  value={renderToString(
                    field.value.split('\n').map((v, i) => (
                      <p
                        key={`fake-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: skip
                          i
                        }`}
                      >
                        {v}
                      </p>
                    )),
                  )}
                  preset={plainTextPreset({placeholder: 'Описание инструкции'})}
                />
              );
            }}
          />
        </div>

        <StepDivider onAddManual={() => onClickAdd(-1)} onAddBrowser={() => onClickAddBrowser(0)} />

        <AnimatePresence initial={false}>
          {fields.map((step, index) => (
            <motion.div
              {...stepMotionProps}
              key={`step-${step.id}`}
              layout="position"
              layoutId={`step-${step.id}`}
              data-step-id={step.id}
            >
              <motion.div {...stepEntryMotionProps} className="-mx-24 overflow-hidden px-24">
                <ErrorBoundary
                  fallback={
                    <StepErrorComponent
                      index={
                        visibleFields.findIndex((visibleStep) => visibleStep.id === step.id) + 1
                      }
                    />
                  }
                >
                  <StepProvider>
                    <StepRenderer
                      index={
                        visibleFields.findIndex((visibleStep) => visibleStep.id === step.id) + 1
                      }
                      step={step.payload}
                      toolbar={{
                        type: 'default',
                        right: (
                          <StepToolbarRight
                            step={step.payload}
                            onMoveUp={() => onStepMove(index, index - 1)}
                            onMoveDown={() => onStepMove(index, index + 1)}
                            onUpdate={(data) => onStepUpdate(index, {...step, payload: data})}
                            onDuplicate={() => onStepDuplicate(index)}
                            onDelete={() => onStepDelete(index)}
                          />
                        ),
                      }}
                      caption={
                        <Controller
                          control={control}
                          key={`step-tiptap-${step.id}`}
                          name={`steps.${index}.payload.element.text`}
                          render={({field}) => {
                            return (
                              <Tiptap
                                {...field}
                                value={renderToString(
                                  (field.value || '').split('\n').map((v, i) => (
                                    <p
                                      key={`fake-${
                                        // biome-ignore lint/suspicious/noArrayIndexKey: skip
                                        i
                                      }`}
                                    >
                                      {v}
                                    </p>
                                  )),
                                )}
                                preset={plainTextPreset({placeholder: 'Описание шага'})}
                              />
                            );
                          }}
                        />
                      }
                      onUpdate={(data) => onViewportUpdate(index, data)}
                    />
                  </StepProvider>
                  <StepDivider
                    onAddManual={() => onClickAdd(index)}
                    onAddBrowser={() => onClickAddBrowser(index + 1)}
                  />
                </ErrorBoundary>
              </motion.div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </form>
  );
};
